import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, RefreshCw, CheckCircle2, CreditCard, PenLine, Bell, LogOut } from "lucide-react";
import AdminCalendar from "@/components/site/AdminCalendar";
import AdminReviews from "@/components/site/AdminReviews";
import AdminDiscountCodes from "@/components/site/AdminDiscountCodes";
import AdminPricing from "@/components/site/AdminPricing";
import AdminGiftCards from "@/components/site/AdminGiftCards";
import AdminDailyPricing from "@/components/site/AdminDailyPricing";
import AdminStats from "@/components/site/AdminStats";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const STATUS_LABELS = {
  nuova: "Nuova",
  in_attesa_pagamento: "In attesa di pagamento",
  pagata: "Pagata",
};

const Studio = () => {
  const [code, setCode] = useState(() => localStorage.getItem("ci-admin-code") || "");
  const [authed, setAuthed] = useState(false);
  const [me, setMe] = useState(null);
  const [booting, setBooting] = useState(true);
  const [tab, setTab] = useState("richieste");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(null);
  const [gen, setGen] = useState({ section: "Hero", current_text: "", instructions: "", lang: "it" });
  const [genResult, setGenResult] = useState("");
  const [generating, setGenerating] = useState(false);
  const processedHash = useRef(false);

  const headers = code ? { "X-Admin-Code": code } : {};

  const loadRequests = useCallback(async ({ silent = false } = {}) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/requests`, { headers: code ? { "X-Admin-Code": code } : {} });
      setRequests(data.requests);
      setAuthed(true);
      if (code) localStorage.setItem("ci-admin-code", code);
    } catch {
      setAuthed(false);
      if (!silent) toast.error("Accesso non riuscito. Verifica il codice o accedi con Google.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const loginGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/studio";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch {
      /* noop */
    }
    setMe(null);
    setAuthed(false);
    setCode("");
    localStorage.removeItem("ci-admin-code");
    toast.success("Disconnesso");
  };

  useEffect(() => {
    const hash = window.location.hash || "";
    if (hash.includes("session_id=") && !processedHash.current) {
      processedHash.current = true;
      const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      (async () => {
        try {
          const { data } = await axios.post(`${API}/auth/session`, {}, { headers: { "X-Session-ID": sid } });
          setMe(data);
          window.history.replaceState(null, "", "/studio");
          await loadRequests({ silent: true });
          toast.success(`Benvenuto, ${data.name || data.email}`);
        } catch (e) {
          toast.error(e.response?.data?.detail || "Accesso con Google non riuscito");
          window.history.replaceState(null, "", "/studio");
        } finally {
          setBooting(false);
        }
      })();
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        setMe(data);
        await loadRequests({ silent: true });
      } catch {
        if (code) await loadRequests({ silent: true });
      } finally {
        setBooting(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const approve = async (id) => {    setApproving(id);
    try {
      const { data } = await axios.post(
        `${API}/admin/requests/${id}/approve`,
        { origin_url: window.location.origin },
        { headers }
      );
      toast.success(`Link di pagamento inviati — totale EUR ${data.total.toFixed(2)} (${data.nights} notti)`);
      loadRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'approvazione");
    } finally {
      setApproving(null);
    }
  };

  const markPaid = async (id) => {
    if (!window.confirm("Confermi di aver ricevuto il bonifico per questa richiesta? La prenotazione verrà segnata come pagata e le date bloccate.")) return;
    setApproving(id);
    try {
      const { data } = await axios.post(`${API}/admin/requests/${id}/mark-paid`, {}, { headers });
      toast.success(`Segnata come pagata (bonifico) — EUR ${data.total.toFixed(2)}`);
      loadRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile segnare come pagata");
    } finally {
      setApproving(null);
    }
  };

  const generate = async () => {
    setGenerating(true);
    setGenResult("");
    try {
      const { data } = await axios.post(`${API}/content/generate`, gen, { headers });
      setGenResult(data.text);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generazione non riuscita");
    } finally {
      setGenerating(false);
    }
  };

  const sendReminder = async (id) => {
    try {
      await axios.post(`${API}/admin/reminders/send/${id}`, {}, { headers });
      toast.success("Promemoria pre-check-in inviato");
      loadRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invio promemoria fallito");
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl bg-white border border-[#0D253A]/15 text-sm";

  if (booting) {
    return (
      <div data-testid="studio-booting" className="min-h-screen flex items-center justify-center px-6 pt-20">
        <div className="flex items-center gap-3 text-[#5C6773]">
          <RefreshCw className="w-5 h-5 animate-spin text-[#C86D51]" />
          <span className="text-sm">Verifica accesso…</span>
        </div>
      </div>
    );
  }

  if (!authed) {
    return (
      <div data-testid="studio-login" className="min-h-screen flex items-center justify-center px-6 pt-20">
        <div className="w-full max-w-sm bg-white rounded-3xl border border-[#0D253A]/10 p-8 shadow-xl">
          <h1 className="font-display text-2xl text-[#0D253A]">Studio proprietario</h1>
          <p className="text-sm text-[#5C6773] mt-2">Accedi con il tuo account Google autorizzato per gestire richieste e contenuti.</p>

          <button
            data-testid="studio-google-login"
            onClick={loginGoogle}
            className="mt-6 w-full inline-flex items-center justify-center gap-3 px-6 py-3 bg-white border border-[#0D253A]/20 text-[#0D253A] text-sm font-semibold rounded-full hover:border-[#C86D51] hover:shadow-md transition-all"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Accedi con Google
          </button>

          <div className="my-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-[#5C6773]/60">
            <span className="h-px flex-1 bg-[#0D253A]/10" /> oppure con codice <span className="h-px flex-1 bg-[#0D253A]/10" />
          </div>

          <input
            data-testid="studio-code-input"
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadRequests()}
            placeholder="Codice admin"
            className={inputCls}
          />
          <button
            data-testid="studio-login-button"
            onClick={() => loadRequests()}
            disabled={loading}
            className="mt-4 w-full px-6 py-3 bg-[#0D253A] text-[#F8F5F0] text-sm font-semibold rounded-full hover:bg-[#C86D51] disabled:opacity-60 transition-colors"
          >
            {loading ? "Verifica…" : "Accedi con codice"}
          </button>
        </div>
      </div>
    );
  }

  const tabCls = (name) =>
    `px-5 py-2.5 text-xs font-semibold uppercase tracking-wider rounded-full border transition-colors ${
      tab === name ? "bg-[#0D253A] text-[#F8F5F0] border-[#0D253A]" : "border-[#0D253A]/20 text-[#0D253A]/70 hover:border-[#C86D51]"
    }`;

  return (
    <div data-testid="studio-dashboard" className="min-h-screen px-6 sm:px-12 lg:px-20 pt-28 pb-20 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="font-display text-3xl text-[#0D253A]">Studio proprietario</h1>
        <div className="flex flex-wrap gap-2">
          <button data-testid="studio-tab-requests" onClick={() => setTab("richieste")} className={tabCls("richieste")}>
            Richieste
          </button>
          <button data-testid="studio-tab-calendar" onClick={() => setTab("calendario")} className={tabCls("calendario")}>
            Calendario
          </button>
          <button data-testid="studio-tab-reviews" onClick={() => setTab("recensioni")} className={tabCls("recensioni")}>
            Recensioni
          </button>
          <button data-testid="studio-tab-codes" onClick={() => setTab("codici")} className={tabCls("codici")}>
            Codici sconto
          </button>
          <button data-testid="studio-tab-pricing" onClick={() => setTab("prezzi")} className={tabCls("prezzi")}>
            Prezzi e periodi
          </button>
          <button data-testid="studio-tab-daily" onClick={() => setTab("giornalieri")} className={tabCls("giornalieri")}>
            Tariffe giornaliere
          </button>
          <button data-testid="studio-tab-stats" onClick={() => setTab("statistiche")} className={tabCls("statistiche")}>
            Statistiche
          </button>
          <button data-testid="studio-tab-gifts" onClick={() => setTab("regali")} className={tabCls("regali")}>
            Buoni regalo
          </button>
          <button data-testid="studio-tab-content" onClick={() => setTab("contenuti")} className={tabCls("contenuti")}>
            Contenuti AI
          </button>
          <a
            data-testid="studio-house-rules-pdf"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/house-rules.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2.5 rounded-full border border-[#0D253A]/20 text-xs font-semibold text-[#0D253A]/70 hover:text-[#C86D51] transition-colors"
          >
            Regolamento PDF
          </a>
          <a
            data-testid="studio-wifi-card-pdf"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/wifi-card.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2.5 rounded-full border border-[#0D253A]/20 text-xs font-semibold text-[#0D253A]/70 hover:text-[#C86D51] transition-colors"
          >
            Cartellino Wi-Fi
          </a>
          <button
            data-testid="studio-refresh-button"
            onClick={() => loadRequests()}
            className="p-2.5 rounded-full border border-[#0D253A]/20 text-[#0D253A]/70 hover:text-[#C86D51] transition-colors"
            aria-label="Aggiorna"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          {me && (
            <span data-testid="studio-user-chip" className="inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-[#0D253A]/5 text-xs text-[#0D253A]">
              {me.picture ? (
                <img src={me.picture} alt="" className="w-6 h-6 rounded-full object-cover" referrerPolicy="no-referrer" />
              ) : (
                <span className="w-6 h-6 rounded-full bg-[#C86D51] text-white flex items-center justify-center text-[10px] font-semibold">
                  {(me.name || me.email || "?").slice(0, 1).toUpperCase()}
                </span>
              )}
              <span className="max-w-[140px] truncate font-medium">{me.name || me.email}</span>
            </span>
          )}
          <button
            data-testid="studio-logout-button"
            onClick={logout}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-[#0D253A]/20 text-xs font-semibold text-[#0D253A]/70 hover:text-[#C86D51] transition-colors"
          >
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>
      </div>

      {tab === "richieste" && (
        <div data-testid="studio-requests-list" className="flex flex-col gap-5">
          {requests.length === 0 && <p className="text-sm text-[#5C6773]">Nessuna richiesta per ora.</p>}
          {requests.map((r) => (
            <div key={r.id} data-testid={`studio-request-${r.id.slice(0, 8)}`} className="bg-white rounded-2xl border border-[#0D253A]/10 p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-display text-lg text-[#0D253A]">{r.name}</p>
                  <p className="text-xs text-[#5C6773]">{r.email}</p>
                  <p className="text-sm mt-2 text-[#1A2129]">
                    {r.check_in} → {r.check_out} · {r.guests} ospiti
                    {r.total ? ` · EUR ${r.total.toFixed(2)} (${r.nights} notti)` : ""}
                  </p>
                  {r.message && <p className="text-sm text-[#5C6773] mt-1 italic">"{r.message}"</p>}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span
                    data-testid={`studio-status-${r.id.slice(0, 8)}`}
                    className={`px-3 py-1 text-xs font-semibold rounded-full ${
                      r.status === "pagata"
                        ? "bg-green-100 text-green-800"
                        : r.status === "in_attesa_pagamento"
                        ? "bg-amber-100 text-amber-800"
                        : "bg-[#C86D51]/10 text-[#C86D51]"
                    }`}
                  >
                    {STATUS_LABELS[r.status] || r.status}
                  </span>
                  {r.status !== "pagata" && (
                    <button
                      data-testid={`studio-approve-${r.id.slice(0, 8)}`}
                      onClick={() => approve(r.id)}
                      disabled={approving === r.id}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#C86D51] text-white text-xs font-semibold rounded-full hover:bg-[#0D253A] disabled:opacity-60 transition-colors"
                    >
                      <CreditCard className="w-4 h-4" />
                      {approving === r.id ? "Creazione link…" : r.status === "in_attesa_pagamento" ? "Reinvia link pagamento" : "Approva e invia link pagamento"}
                    </button>
                  )}
                  {r.status === "in_attesa_pagamento" && (
                    <button
                      data-testid={`studio-markpaid-${r.id.slice(0, 8)}`}
                      onClick={() => markPaid(r.id)}
                      disabled={approving === r.id}
                      className="inline-flex items-center gap-2 px-4 py-2 border border-green-600 text-green-700 text-xs font-semibold rounded-full hover:bg-green-600 hover:text-white disabled:opacity-60 transition-colors"
                      title="Usa questo dopo aver ricevuto un bonifico bancario"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Segna come pagata (bonifico)
                    </button>
                  )}
                  {r.status === "pagata" && (
                    <>
                      <span className="inline-flex items-center gap-1 text-xs text-green-700">
                        <CheckCircle2 className="w-4 h-4" /> Pagamento ricevuto
                      </span>
                      <button
                        data-testid={`studio-reminder-${r.id.slice(0, 8)}`}
                        onClick={() => sendReminder(r.id)}
                        className={`inline-flex items-center gap-1.5 px-4 py-1.5 border text-xs font-semibold rounded-full transition-colors ${
                          r.reminder_sent_at
                            ? "border-[#0D253A]/15 text-[#0D253A]/50"
                            : "border-[#C86D51] text-[#C86D51] hover:bg-[#C86D51] hover:text-white"
                        }`}
                        title={r.reminder_sent_at ? `Ultimo invio: ${r.reminder_sent_at}` : ""}
                      >
                        <Bell className="w-3.5 h-3.5" />
                        {r.reminder_sent_at ? "Reinvia promemoria" : "Invia promemoria"}
                      </button>
                    </>
                  )}
                </div>
              </div>
              {r.ai_draft && (
                <details className="mt-4">
                  <summary className="text-xs font-semibold uppercase tracking-wider text-[#C86D51] cursor-pointer flex items-center gap-1">
                    <PenLine className="w-3.5 h-3.5" /> Bozza di risposta AI
                  </summary>
                  <p className="mt-2 text-sm text-[#333D47] whitespace-pre-line bg-[#F8F5F0] rounded-xl p-4 border-l-2 border-[#C86D51]">{r.ai_draft}</p>
                </details>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "calendario" && <AdminCalendar headers={headers} />}

      {tab === "recensioni" && (
        <AdminReviews headers={headers} requests={requests} onRefreshRequests={loadRequests} />
      )}

      {tab === "codici" && <AdminDiscountCodes authHeaders={headers} />}

      {tab === "prezzi" && <AdminPricing authHeaders={headers} />}

      {tab === "giornalieri" && <AdminDailyPricing authHeaders={headers} />}

      {tab === "statistiche" && <AdminStats authHeaders={headers} />}

      {tab === "regali" && <AdminGiftCards authHeaders={headers} />}

      {tab === "contenuti" && (
        <div data-testid="studio-content-generator" className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl border border-[#0D253A]/10 p-6 flex flex-col gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">Sezione del sito</label>
              <select
                data-testid="studio-section-select"
                value={gen.section}
                onChange={(e) => setGen({ ...gen, section: e.target.value })}
                className={inputCls}
              >
                {["Hero", "Descrizione appartamento", "Posizione", "Meta description SEO", "Annuncio per portali"].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">Testo attuale (facoltativo)</label>
              <textarea
                data-testid="studio-current-text"
                rows={4}
                value={gen.current_text}
                onChange={(e) => setGen({ ...gen, current_text: e.target.value })}
                className={`${inputCls} resize-none`}
                placeholder="Incolla qui il testo da migliorare…"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">Istruzioni</label>
              <input
                data-testid="studio-instructions-input"
                type="text"
                value={gen.instructions}
                onChange={(e) => setGen({ ...gen, instructions: e.target.value })}
                className={inputCls}
                placeholder="Es. tono più informale, max 60 parole…"
              />
            </div>
            <div className="flex gap-2">
              {["it", "en"].map((l) => (
                <button
                  key={l}
                  data-testid={`studio-lang-${l}`}
                  onClick={() => setGen({ ...gen, lang: l })}
                  className={`px-4 py-2 text-xs font-semibold uppercase rounded-full border transition-colors ${
                    gen.lang === l ? "bg-[#0D253A] text-[#F8F5F0] border-[#0D253A]" : "border-[#0D253A]/20 text-[#0D253A]/70"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
            <button
              data-testid="studio-generate-button"
              onClick={generate}
              disabled={generating}
              className="mt-2 px-6 py-3.5 bg-[#C86D51] text-white text-sm font-semibold rounded-full hover:bg-[#0D253A] disabled:opacity-60 transition-colors"
            >
              {generating ? "Claude sta scrivendo…" : "Genera con Claude"}
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-[#0D253A]/10 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg text-[#0D253A]">Testo generato</h3>
              {genResult && (
                <button
                  data-testid="studio-copy-button"
                  onClick={() => {
                    navigator.clipboard.writeText(genResult);
                    toast.success("Copiato negli appunti");
                  }}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#C86D51] hover:text-[#0D253A] transition-colors"
                >
                  <Copy className="w-4 h-4" /> Copia
                </button>
              )}
            </div>
            <p data-testid="studio-generated-text" className="text-sm text-[#333D47] whitespace-pre-line leading-relaxed min-h-40">
              {genResult || "Il testo generato da Claude apparirà qui."}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Studio;
